// Routing + the spectator gate. The dashcam render route is chromeless and
// authorised by its clip token, so it sits outside the session gate.
import { lazy, Suspense, useState } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'wouter';
import { usePresenceRoom } from './lib/presence';
import { SessionProvider, useSession } from './lib/session';
import { nextTheme, storedTheme, applyTheme, type ThemeChoice } from './lib/theme';
import { Icon, type IconName } from './ui/Icon';
import { Empty, ErrorBoundary, Spinner } from './ui/primitives';
import { ToastProvider } from './ui/Toasts';
import { Logo } from './ui/Logo';
import { LoginView } from './views/Login';
import { CastView } from './views/Cast';
import { AgentView } from './views/Agent';
import { CommsView } from './views/Comms';
import { FeedbackView } from './views/Feedback';
import { CompanyView } from './views/Company';
import { AdminView } from './views/Admin';

// Map views pull in MapLibre + deck.gl; load them on demand.
const WorldView = lazy(() => import('./views/World').then((m) => ({ default: m.WorldView })));
const DriveView = lazy(() => import('./views/Drive').then((m) => ({ default: m.DriveView })));
const RenderDashcamView = lazy(() => import('./views/RenderDashcam').then((m) => ({ default: m.RenderDashcamView })));

export function App() {
  return (
    <Switch>
      <Route path="/render/dashcam">
        <Suspense fallback={null}>
          <RenderDashcamView />
        </Suspense>
      </Route>
      <Route>
        <SessionProvider>
          <ToastProvider>
            <Gate />
          </ToastProvider>
        </SessionProvider>
      </Route>
    </Switch>
  );
}

function Gate() {
  const session = useSession();
  if (session.loading) return <Splash />;
  if (!session.token) return <LoginView />;
  return <Shell />;
}

function Splash() {
  return (
    <div className="splash">
      <Logo />
      <Spinner label="Opening the gates" />
    </div>
  );
}

type NavItem = { href: string; label: string; icon: IconName; admin?: boolean };
const NAV: NavItem[] = [
  { href: '/', label: 'World', icon: 'map' },
  { href: '/cast', label: 'Cast', icon: 'people' },
  { href: '/comms', label: 'Comms', icon: 'chat' },
  { href: '/feedback', label: 'Feedback', icon: 'bug' },
  { href: '/company', label: 'Company', icon: 'building' },
  { href: '/drive', label: 'Drive', icon: 'wheel', admin: true },
  { href: '/admin', label: 'Admin', icon: 'shield', admin: true },
];

function isActive(href: string, location: string): boolean {
  if (href === '/') return location === '/';
  if (href === '/cast') return location.startsWith('/cast') || location.startsWith('/agent');
  return location.startsWith(href);
}

function Shell() {
  const session = useSession();
  const [location] = useLocation();
  const watchers = usePresenceRoom('world');
  const [theme, setTheme] = useState<ThemeChoice>(storedTheme);
  const items = NAV.filter((n) => !n.admin || session.isAdmin);
  const themeIcon: IconName = theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'eye';

  return (
    <div className={`shell ${location === '/' || location.startsWith('/drive') ? 'is-fullbleed' : ''}`}>
      <nav className="nav" aria-label="Main">
        <Link href="/" className="nav-brand" aria-label="AllAware Play home">
          <Logo small />
        </Link>
        <ul className="nav-items">
          {items.map((n) => (
            <li key={n.href}>
              <Link href={n.href} className={`nav-item ${isActive(n.href, location) ? 'is-active' : ''}`} aria-current={isActive(n.href, location) ? 'page' : undefined}>
                <Icon name={n.icon} size={20} />
                <span>{n.label}</span>
              </Link>
            </li>
          ))}
        </ul>
        <div className="nav-foot">
          <span className="nav-watchers" title="Spectators watching right now">
            <span className="live-dot" />
            {watchers ? watchers.online : '·'} watching
          </span>
          <button
            type="button"
            className="icon-btn theme-btn"
            aria-label={`Theme: ${theme}`}
            title={`Theme: ${theme} (click to change)`}
            onClick={() => {
              const t = nextTheme(theme);
              applyTheme(t);
              setTheme(t);
            }}
          >
            <Icon name={themeIcon} size={18} />
          </button>
          <button type="button" className="icon-btn" aria-label="Sign out" title={`Signed in as ${session.label ?? session.role} — sign out`} onClick={session.signOut}>
            <Icon name="logout" size={18} />
          </button>
        </div>
      </nav>
      <main className="shell-main">
        <ErrorBoundary key={location} label={location} onUnauthenticated={session.signOut}>
          <Suspense fallback={<div className="page-loading"><Spinner /></div>}>
            <Switch>
              <Route path="/">
                <WorldView />
              </Route>
              <Route path="/login">
                <Redirect to="/" />
              </Route>
              <Route path="/cast">
                <CastView />
              </Route>
              <Route path="/agent/:key">{(params) => <AgentView personaKey={params.key} />}</Route>
              <Route path="/comms">
                <CommsView />
              </Route>
              <Route path="/feedback">
                <FeedbackView />
              </Route>
              <Route path="/company">
                <CompanyView />
              </Route>
              <Route path="/drive">{session.isAdmin ? <DriveView /> : <AdminOnly />}</Route>
              <Route path="/admin">{session.isAdmin ? <AdminView /> : <AdminOnly />}</Route>
              <Route>
                <div className="page">
                  <Empty icon="pin" title="Nothing at this address">
                    <Link href="/">Back to the world map</Link>
                  </Empty>
                </div>
              </Route>
            </Switch>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function AdminOnly() {
  return (
    <div className="page">
      <Empty icon="key" title="Admins only">
        Sign in with the admin passcode to drive or change the simulation.
      </Empty>
    </div>
  );
}
